import { ErrorModal } from "@dexteel/mesf-core";
import { Autocomplete, Box, CircularProgress, TextField } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { FieldError } from "react-hook-form";
import { DelayArea } from "../models/DelayArea";
import { getDelayAreas } from "../repositories/DelayAreaRepository";

type Props = {
	label: string;
	value: number | null;
	onChange?: Function;
	fieldError?: FieldError | undefined;
	disabled?: boolean;
	required?: boolean;
	showAllOption?: boolean;
	includeDelayAssets?: boolean;
	allowedAreaIds?: number[];
};

const useDelayAreas = (
	includeDelayAssets: boolean,
	onError?: (error: Error) => void,
) => {
	return useQuery<DelayArea[], Error>({
		queryKey: ["delayAreas", includeDelayAssets],
		queryFn: () => getDelayAreas(includeDelayAssets),
		onError: onError,
	});
};

const ALL_OPTION: DelayArea = { AssetId: -1, AssetName: "All" };

export const DelayAreaSelector = ({
	label,
	value,
	onChange = () => {},
	fieldError,
	disabled = false,
	required = false,
	showAllOption = false,
	includeDelayAssets = false,
	allowedAreaIds,
}: Props) => {
	const [error, setError] = useState("");

	const { data: delayAreas, isLoading } = useDelayAreas(
		includeDelayAssets,
		(error) => setError(error.message),
	);

	const filteredAreas = allowedAreaIds
		? (delayAreas || []).filter((da) => allowedAreaIds.includes(da.AssetId))
		: delayAreas || [];

	const options = showAllOption
		? [ALL_OPTION, ...filteredAreas]
		: filteredAreas;

	const selectedDelayArea =
		value === null && showAllOption
			? ALL_OPTION
			: filteredAreas.find((da) => da.AssetId === value) || null;

	return (
		<>
			<Autocomplete
				id="clear-on-escape"
				clearOnEscape
				options={options}
				getOptionLabel={(option) => option.AssetName}
				disabled={disabled}
				renderInput={(params) => (
					<TextField
						{...params}
						required={required}
						label={label}
						error={!!fieldError}
						helperText={fieldError?.message}
						InputProps={{
							...params.InputProps,
							endAdornment: (
								<>
									{isLoading ? (
										<CircularProgress color="inherit" size={20} />
									) : (
										params.InputProps.endAdornment
									)}
								</>
							),
						}}
					/>
				)}
				value={selectedDelayArea}
				onChange={(event, newValue) => {
					onChange(
						newValue?.AssetId === -1 ? null : (newValue?.AssetId ?? null),
					);
				}}
			/>
			<ErrorModal
				error={error}
				onHide={() => setError("")}
				title="Error loading assets"
			/>
		</>
	);
};
